import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RESET_PATHS,
  checkPassword,
  passwordAcceptable,
  passwordResetPreviewSchema,
  resetPasswordResultSchema,
  type PasswordResetPreview,
  type ResetOutcome,
} from '@forecastify/shared';
import { Button, TextInput } from '@forecastify/ui';
import { apiUrl } from '../../api';
import { useT, type MessageKey, type Translate } from '../../i18n';
import { SIGN_IN_PATH } from '../../routes';
import { AuthField, AuthFrame } from './auth-frame';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `/reset-password/:token` — the link's other end. C18.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS PAGE RENDERS FOR SOMEBODY WHO CANNOT SIGN IN, so it is `chrome: 'bare'`, it is
 * `signedOutOk`, and it fetches WITHOUT a session.** Exactly `invite-screen.tsx`'s situation and for
 * exactly its reason: `session.request` attaches a bearer token and a workspace header that do not
 * exist here, and `AppShell` needs a membership the visitor may not have.
 *
 * ⚠️ **Three states before the form, and each gets its own sentence.** A link that is expired, spent
 * or unrecognised is not "an error" — it is a specific thing that happened, with a different next
 * step each time, and a page saying *"something went wrong"* leaves somebody clicking the same dead
 * link again.
 *
 * ⚠️⚠️ **SUCCESS DOES NOT SIGN THEM IN, AND THAT IS DELIBERATE — IT IS THE OPPOSITE OF THE
 * INVITATION.** `invite-screen.tsx` sets a password and signs in with it in one click, because the
 * token proved control of the address and the alternative was five steps. Here, redeeming the token
 * **revokes every session for the identity** (`0028`), which is the whole point of a reset: the
 * person who might have had the old password is signed out. Handing back a fresh session in the same
 * breath would be fine for the legitimate owner and would also be the one thing that makes a stolen
 * reset link immediately useful — the attacker never has to know the password they just set. So the
 * page sends them to sign in with it, which also proves to them that it works.
 */

function outcomeMessage(t: Translate, outcome: ResetOutcome): string {
  /* One `t()` per branch, literal keys — the catalogue is typed and a computed key defeats that. */
  if (outcome === 'expired') return t('reset.expired');
  if (outcome === 'used') return t('reset.used');
  if (outcome === 'weak_password') return t('reset.weak');
  return t('reset.unknown');
}

const RULE_LABEL: Readonly<Record<string, MessageKey>> = {
  length: 'account.password.rule.length',
  letters: 'account.password.rule.letters',
  digits: 'account.password.rule.digits',
  match: 'account.password.rule.match',
};

export function ResetPasswordScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { token = '' } = useParams<{ token: string }>();

  const [preview, setPreview] = useState<PasswordResetPreview | null>(null);
  const [missing, setMissing] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const load = useCallback(async (): Promise<void> => {
    const response = await fetch(apiUrl(`/auth/password/reset/${encodeURIComponent(token)}`)).catch(
      () => null,
    );

    if (response?.ok !== true) {
      setMissing(true);
      return;
    }
    /* ⚠️ Parsed against the contract rather than cast. A 200 that is not a preview is a failure, not
     * silence — `routes.test.tsx` holds that rule for the shell's screens and it is the same rule. */
    const parsed = passwordResetPreviewSchema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) {
      setMissing(true);
      return;
    }
    setPreview(parsed.data);
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = (): void => {
    void (async () => {
      setBusy(true);
      setFailure(null);

      const response = await fetch(apiUrl(`/auth/password/reset/${encodeURIComponent(token)}`), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password }),
      }).catch(() => null);

      const parsed = resetPasswordResultSchema.safeParse(
        response === null ? null : await response.json().catch(() => null),
      );

      setBusy(false);

      if (!parsed.success) {
        setFailure(t('error.unavailable'));
        return;
      }
      if (parsed.data.outcome !== 'reset') {
        setFailure(outcomeMessage(t, parsed.data.outcome));
        return;
      }
      setDone(true);
    })();
  };

  if (missing) {
    return (
      <Frame>
        <p role="alert" className="text-[13px] leading-relaxed">
          {t('reset.unknown')}
        </p>
        {/* ⚠️ Straight back to asking for a new one, because that is the only useful next step from
            a dead link — not to sign-in, which is where they already failed. */}
        <Button
          size="lg"
          className="w-full"
          onClick={() => {
            void navigate(PASSWORD_RESET_PATHS.request);
          }}
        >
          {t('reset.requestAnother')}
        </Button>
      </Frame>
    );
  }

  if (preview === null) {
    /* ⚠️ The loading state says what it is waiting for. `state-rules.md` §1. */
    return (
      <Frame>
        <p role="status" className="text-muted-foreground text-[12.5px]">
          {t('state.loading')}
        </p>
      </Frame>
    );
  }

  if (done) {
    return (
      <Frame>
        <p role="status" className="text-[13px] leading-relaxed">
          {t('reset.done')}
        </p>
        {/**
         * ⚠️ **It says the other sessions were ended, because they were.** `0028` revokes every
         * session for the identity on redemption — somebody signed in on a phone is now signed out,
         * and finding that out later without being told reads as a fault rather than as the feature
         * they just used.
         */}
        <p className="text-muted-foreground text-[12.5px] leading-relaxed">
          {t('reset.doneSessions')}
        </p>
        <Button
          size="lg"
          className="w-full"
          onClick={() => {
            void navigate(SIGN_IN_PATH);
          }}
        >
          {t('reset.signIn')}
        </Button>
      </Frame>
    );
  }

  if (preview.state !== 'pending') {
    return (
      <Frame>
        <p role="alert" className="text-[13px] leading-relaxed">
          {preview.state === 'expired' ? t('reset.expired') : t('reset.used')}
        </p>
        <Button
          size="lg"
          className="w-full"
          onClick={() => {
            void navigate(PASSWORD_RESET_PATHS.request);
          }}
        >
          {t('reset.requestAnother')}
        </Button>
      </Frame>
    );
  }

  return (
    <Frame>
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <p className="text-muted-foreground text-[12.5px] leading-relaxed">{t('reset.lead')}</p>

        {/**
         * ⚠️⚠️ **MASKED, AND THE SERVER IS WHAT MASKS IT — the invitation page shows its address in
         * FULL and the difference is who is holding the link.** An invitation must state the address
         * to sign in with, or the refusal that follows is a mystery. A reset link goes to somebody
         * who already knows theirs; printing it whole would turn a forwarded or shoulder-surfed link
         * into a confirmed *"this person has an account here"* — the same existence leak
         * `/auth/password/forgot` refused to make one screen earlier. `password-reset.ts` carries
         * the argument, and `maskEmail` lives there so no client can decide otherwise.
         */}
        <p className="bg-muted/50 rounded-md px-3 py-2 text-[12px]">{preview.maskedEmail}</p>

        <AuthField label={t('reset.password')}>
          <TextInput
            type="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </AuthField>
        <AuthField label={t('reset.confirm')}>
          <TextInput
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => {
              setConfirmation(event.target.value);
            }}
          />
        </AuthField>

        {/**
         * ⚠️ **The REAL policy, from `PASSWORD_RULES` in `@forecastify/shared`** — the same list the
         * account screen and the invitation page render, whose own test reads `config.toml`. A
         * hand-written checklist here would be a fourth opinion, and the one that disagrees.
         */}
        <ul className="flex flex-col gap-0.5">
          {Object.entries(checkPassword(password, confirmation)).map(([rule, met]) => (
            <li
              key={rule}
              className={
                met ? 'text-[11.5px] text-emerald-600' : 'text-muted-foreground text-[11.5px]'
              }
            >
              {met ? '✓' : '·'}{' '}
              {t(RULE_LABEL[rule] ?? 'reset.password', { min: PASSWORD_MIN_LENGTH })}
            </li>
          ))}
        </ul>

        {failure !== null && (
          <p role="alert" className="text-destructive text-[12px]">
            {failure}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={busy || !passwordAcceptable(password, confirmation)}
        >
          {busy ? t('reset.working') : t('reset.submit')}
        </Button>

        <Link
          to={SIGN_IN_PATH}
          className="text-muted-foreground hover:text-foreground text-center text-[11.5px] underline"
        >
          {t('forgot.backToSignIn')}
        </Link>
      </form>
    </Frame>
  );
}

/** The card. ⚠️ One marker for every state, because they are all this screen. */
function Frame({ children }: { readonly children: React.ReactNode }) {
  const t = useT();
  return (
    <AuthFrame screen="reset-password" title={t('reset.title')}>
      {children}
    </AuthFrame>
  );
}
